import getMessagesByKeys from './i18n/getMessagesByKeys';
import notification from './notification';
import ResponseErrorTypes from '../constants/ResponseErrorTypes';
import ResponseStatus from '../constants/ResponseStatus';
import { UnauthenticatedPaths } from '../constants/Paths';
import SearchParams from '../constants/SearchParams';
import getFormattedDateTime, { getI18nOptionsForDateFormatting } from './getFormattedDateTime';

export const getFirstError = (response) => {
    const { errors, responseStatus } = response;
    return responseStatus === ResponseStatus.FAILURE && Array.isArray(errors) && errors.length > 0
        ? errors[0]
        : undefined;
};

const convertErrorStateToSearchParams = (error) => {
    const searchParams = {
        [SearchParams.ERROR_TYPE]: error.type,
    };
    const queryParams = new URLSearchParams(searchParams);
    if (error.id) {
        queryParams.append(SearchParams.ERROR_ID, error.id);
    }
    if (error.timestamp) {
        queryParams.append(SearchParams.ERROR_TIMESTAMP, error.timestamp);
    }
    return queryParams.toString();
};

/**
 * Navigates to the error page with an error object.
 *
 * Error id and timestamps are not strictly enforced to support errors that are generated client-side, which do not have ids or timestamps
 * @see {RegisterPage}
 *
 * @param {object} history
 * @param {object} error
 */
export const navigateToErrorPage = (history, error) => {
    const errorSearchParams = convertErrorStateToSearchParams(error);
    history.replace(`${UnauthenticatedPaths.ERROR}?${errorSearchParams}`);
};

const getMessageKeyForError = (error) => {
    return `error.${error.type.toLowerCase()}`;
};

export const getErrorMessage = async (error, language) => {
    const messageKey = getMessageKeyForError(error);
    const [errorMessage] = await getMessagesByKeys([messageKey], language);
    return errorMessage;
};

export const processErrors = (errors = []) => {
    const result = {
        notifications: [],
        password: undefined,
    };

    const passwordErrors = [];
    errors.forEach((error) => {
        if (ResponseErrorTypes.VEEVAID_INVALID_USER_PASSWORD === error.type) {
            passwordErrors.push(error.message);
        } else {
            result.notifications.push(error);
        }
    });

    if (passwordErrors.length) {
        result.password = passwordErrors.join(`\r\n`);
    }

    return result;
};

const notificationErrorLogs = new Set();

export const showErrorNotification = async (errors = [], i18n = {}) => {
    const { language } = i18n;
    const processedErrors = processErrors(errors);
    const errorId = processedErrors.notifications[0]?.id;

    if (!errors.length || notificationErrorLogs.has(errorId)) {
        return;
    }

    notificationErrorLogs.add(errorId);
    const errorMessageKeys = ['error.veevaid_unexpected_error'];
    processedErrors.notifications.forEach((error) => {
        errorMessageKeys.push(getMessageKeyForError(error));
    });

    const localizedMessages = await getMessagesByKeys(errorMessageKeys, language);

    const errorTypeToMessage = errorMessageKeys.reduce((map, msgKey, index) => {
        // eslint-disable-next-line no-param-reassign
        map[msgKey] = localizedMessages[index];
        return map;
    }, {});

    const unexpectedErrorBannerTitle = localizedMessages[0];
    const closeNotification = () => {
        notificationErrorLogs.delete(errorId);
    };
    processedErrors.notifications.forEach((error) => {
        const i18nOptions = getI18nOptionsForDateFormatting(i18n);
        const localizedTimestamp = getFormattedDateTime(error.timestamp, i18nOptions);
        const messageKey = getMessageKeyForError(error);
        const isUnexpectedError = messageKey === 'error.veevaid_unexpected_error';
        let localizedErrorMessageFromType = '';

        if (!isUnexpectedError) {
            localizedErrorMessageFromType =
                errorTypeToMessage[messageKey] ||
                errorTypeToMessage['error.veevaid_unexpected_error'];
        }

        notification.error(
            unexpectedErrorBannerTitle,
            localizedErrorMessageFromType,
            error.id,
            localizedTimestamp,
            closeNotification,
        );
    });
};

export const showErrorNotificationFromResponse = (response, i18n) =>
    showErrorNotification(response.errors, i18n);
